import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import type { AddCartItemInput, CartStore } from "../types/cart.types";

export const useCartStore = create<CartStore>()(
  persist(
    (set) => ({
      items: [],
      isOpen: false,

      hydrateCartItems: (items) => {
        set({ items: Array.isArray(items) ? items : [] });
      },

      addToCart: (item: AddCartItemInput) => {
        set((state) => {
          const quantity = item.quantity ?? 1;
          const existingItem = state.items.find(
            (cartItem) =>
              cartItem.idProduct === item.idProduct &&
              cartItem.idProductVariant === item.idProductVariant
          );

          if (!existingItem) {
            return {
              items: [{ ...item, quantity, attributes: item.attributes || [] }, ...state.items],
            };
          }

          const maxQuantity = item.stock ?? 999;
          const nextItems = state.items.map((cartItem) => {
            if (
              cartItem.idProduct !== item.idProduct ||
              cartItem.idProductVariant !== item.idProductVariant
            ) {
              return cartItem;
            }

            const nextQuantity = Math.min(cartItem.quantity + quantity, maxQuantity);
            return { ...cartItem, quantity: nextQuantity };
          });

          return { items: nextItems };
        });
      },

      removeFromCart: (idProduct, idProductVariant) => {
        set((state) => ({
          items: state.items.filter(
            (item) => !(item.idProduct === idProduct && item.idProductVariant === idProductVariant)
          ),
        }));
      },

      updateQuantity: (idProduct, idProductVariant, quantity) => {
        set((state) => ({
          items: state.items.map((item) => {
            if (item.idProduct !== idProduct || item.idProductVariant !== idProductVariant) {
              return item;
            }

            const maxQuantity = item.stock ?? 999;
            const safeQuantity = Math.min(Math.max(quantity, 1), maxQuantity);
            return { ...item, quantity: safeQuantity };
          }),
        }));
      },

      clearCart: () => {
        set({ items: [], isOpen: false });
      },

      openCart: () => {
        set({ isOpen: true });
      },

      closeCart: () => {
        set({ isOpen: false });
      },

      toggleCart: () => {
        set((state) => ({ isOpen: !state.isOpen }));
      },
    }),
    {
      name: "shopping_cart",
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ items: state.items }),
    }
  )
);

// Sincroniza el carrito entre pestañas/ventanas escuchando cambios en localStorage.
// Usa el evento "storage" nativo (compatible con todos los navegadores y móviles).
if (typeof window !== "undefined") {
  window.addEventListener("storage", (event) => {
    if (event.key !== "shopping_cart" || event.newValue === null) return;
    useCartStore.persist.rehydrate();
  });
}

